import {
    type GetBranchComparisonQuery,
    type GetRevenueDynamicsQuery,
    type GetSalesTransactionsQuery,
    type GetTopBrandsQuery,
    type GetTopCategoriesQuery,
    type GetTopProductsQuery,
} from './queries';
import {
    type GetBranchComparisonResponse,
    type GetHourlyActivityResponse,
    type GetRevenueDynamicsResponse,
    type GetRevenueSummaryResponse,
    type GetSalesTransactionsResponse,
    type GetTopBrandsResponse,
    type GetTopCategoriesResponse,
    type GetTopProductsResponse,
} from './responses';
import { SalesEndpoints } from './salesEndpoints';
import type { ISalesService } from './ISalesService';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import { getAxiosClient } from '../../client';
import {
    toBranchComparison,
    toPopularBrands,
    toRevenueDynamics,
    toRevenueSummary,
    toSalesTransactionsPage,
    toTopCategories,
    toTopProducts,
} from './salesMapping';

type SalesServiceOptions = {
    baseUrl: string;
};

export class SalesService implements ISalesService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: SalesServiceOptions) {
        this.baseUrl = baseUrl;
    }

    public async getRevenueSummary(): Promise<
        ApiResponse<GetRevenueSummaryResponse>
    > {
        const url = SalesEndpoints.revenueSummary();
        const httpRequest = getAxiosClient({ baseURL: this.baseUrl }).get(url);
        const result =
            await makeApiRequest<GetRevenueSummaryResponse>(httpRequest);
        return result.success
            ? { success: true, response: toRevenueSummary(result.response) }
            : result;
    }

    public async getRevenueDynamics(
        query: GetRevenueDynamicsQuery,
    ): Promise<ApiResponse<GetRevenueDynamicsResponse>> {
        // bos-api binds query keys case-insensitively, so our camelCase
        // `from` / `to` / `productId` bind to `From` / `To` / `ProductId`.
        const url = SalesEndpoints.revenueDynamics();
        const httpRequest = getAxiosClient({ baseURL: this.baseUrl }).get(url, {
            params: query,
        });
        const result =
            await makeApiRequest<GetRevenueDynamicsResponse>(httpRequest);
        return result.success
            ? { success: true, response: toRevenueDynamics(result.response) }
            : result;
    }

    public async getTopProducts(
        query: GetTopProductsQuery,
    ): Promise<ApiResponse<GetTopProductsResponse>> {
        // bos-api binds query keys case-insensitively, so our camelCase
        // `from` / `to` bind to `From` / `To`.
        const url = SalesEndpoints.topProducts();
        const httpRequest = getAxiosClient({ baseURL: this.baseUrl }).get(url, {
            params: query,
        });
        const result =
            await makeApiRequest<GetTopProductsResponse>(httpRequest);
        return result.success
            ? { success: true, response: toTopProducts(result.response) }
            : result;
    }

    public async getTopBrands(
        query: GetTopBrandsQuery,
    ): Promise<ApiResponse<GetTopBrandsResponse>> {
        const url = SalesEndpoints.topBrands();
        const httpRequest = getAxiosClient({ baseURL: this.baseUrl }).get(url, {
            params: query,
        });
        const result = await makeApiRequest<GetTopBrandsResponse>(httpRequest);
        return result.success
            ? { success: true, response: toPopularBrands(result.response) }
            : result;
    }

    public async getHourlyActivity(): Promise<
        ApiResponse<GetHourlyActivityResponse>
    > {
        const url = SalesEndpoints.hourlyActivity();
        const httpRequest = getAxiosClient({ baseURL: this.baseUrl }).get(url);
        const result =
            await makeApiRequest<GetHourlyActivityResponse>(httpRequest);
        return result;
    }

    public async getBranchComparison(
        query: GetBranchComparisonQuery,
    ): Promise<ApiResponse<GetBranchComparisonResponse>> {
        const url = SalesEndpoints.branchComparison();
        const httpRequest = getAxiosClient({ baseURL: this.baseUrl }).get(url, {
            params: query,
        });
        const result =
            await makeApiRequest<GetBranchComparisonResponse>(httpRequest);
        return result.success
            ? { success: true, response: toBranchComparison(result.response) }
            : result;
    }

    public async getTopCategories(
        query: GetTopCategoriesQuery,
    ): Promise<ApiResponse<GetTopCategoriesResponse>> {
        const url = SalesEndpoints.topCategories();
        const httpRequest = getAxiosClient({ baseURL: this.baseUrl }).get(url, {
            params: query,
        });
        const result =
            await makeApiRequest<GetTopCategoriesResponse>(httpRequest);
        return result.success
            ? { success: true, response: toTopCategories(result.response) }
            : result;
    }

    public async getSalesTransactions(
        query: GetSalesTransactionsQuery,
    ): Promise<ApiResponse<GetSalesTransactionsResponse>> {
        // bos-api names its query params PascalCase (`From`, `To`, `Page`,
        // `PageSize`, `SellerName`) but binds them case-insensitively, so
        // the camelCase keys we send bind as they are; its JSON responses
        // are already camelCase.
        const url = SalesEndpoints.salesTransactions();
        const httpRequest = getAxiosClient({ baseURL: this.baseUrl }).get(url, {
            params: query,
        });
        const result =
            await makeApiRequest<GetSalesTransactionsResponse>(httpRequest);
        return result.success
            ? {
                  success: true,
                  response: toSalesTransactionsPage(result.response),
              }
            : result;
    }
}
